import type { BalanceSlot } from '@sportninja/common/services/teamBalance'
import {
  isInvitedSlot,
  isOccupiedLine,
  isOpenPlayerSlot,
} from './balanceRowKind'

/** The toolbar's "Show:" quick filter. */
export type BalanceRowFilter = 'all' | 'owes' | 'paid' | 'open' | 'invited'

/**
 * SN-8346 — the toolbar's row predicate, lifted VERBATIM out of `BalanceTab`'s
 * `rows` memo so the Display Cancelled toggle can ask the same question the
 * sheet answers ("would this slot survive the current Show filter?") instead of
 * a second copy that could drift from it.
 *
 * Note what this means for a CANCELLED slot: the server sets its expected to 0,
 * so `owed = −paid` (BalanceLedger) — never positive. It therefore matches
 * All and Paid up, and never Owes money or Open slots. That asymmetry is the
 * whole reason the toggle can look dead; see `cancelledToggleState`.
 *
 * Scope: this is TOOLBAR eligibility only. AG Grid's own column filters run
 * afterwards inside the grid and can still hide a row this admits.
 */
export const matchesRowFilter = (
  slot: BalanceSlot,
  rowFilter: BalanceRowFilter
): boolean => {
  // Sep 23 — "Open slots" means spots a PLAYER can take: never an official's line,
  // and (Jeff, Oct 1) never a slot held for an invited player.
  if (rowFilter === 'open') {
    return isOpenPlayerSlot(slot)
  }
  // Jeff, Oct 1 — slots held for invited players who have not registered yet.
  if (rowFilter === 'invited') {
    return isInvitedSlot(slot)
  }
  if (rowFilter === 'owes') {
    return Number(slot.owed) > 0
  }
  // An official's line that is settled is paid up like a player's share.
  if (rowFilter === 'paid') {
    return isOccupiedLine(slot) && Number(slot.owed) <= 0
  }

  return true
}

/**
 * Sep 23 — a team official's $0 candidate row owes nothing, has paid nothing and is
 * not a spot to fill, so it answers only "All".
 */
export const candidateMatchesRowFilter = (
  rowFilter: BalanceRowFilter
): boolean => rowFilter === 'all'
